import {
  Progress as AntProgress,
  ProgressProps as AntProgressProps,
} from 'antd';
import styled from 'styled-components';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';

export interface ProgressBarProps extends Omit<
  AntProgressProps,
  'type' | 'steps' | 'strokeColor' | 'trailColor'
> {
  /** Progress percentage (0-100) */
  percent: number;
  /** Height of the progress bar in pixels */
  height?: number;
  /** Whether to show the percentage text */
  showInfo?: boolean;
  /** Custom color for the progress fill (defaults to success green) */
  color?: string;
  /** Custom color for the track background */
  trackColor?: string;
}

const Wrapper = styled.div<{ $height: number }>`
  width: 100%;

  .ant-progress {
    font-family: ${typography.fontFamily.primary};
  }

  .ant-progress-inner {
    border-radius: ${({ $height }) => $height / 2}px;
  }

  .ant-progress-bg {
    border-radius: ${({ $height }) => $height / 2}px;
    transition: width 0.3s ease;
  }

  .ant-progress-text {
    font-family: ${typography.fontFamily.primary};
    font-size: ${typography.body.small.fontSize}px;
    color: ${colors.neutral[700]};
  }
`;

/**
 * ProgressBar component based on Figma Design System
 *
 * A simple horizontal progress bar that shows completion percentage.
 *
 * @example
 * ```tsx
 * import { ProgressBar } from '@libertify/ui';
 *
 * <ProgressBar percent={30} />
 * <ProgressBar percent={75} showInfo />
 * <ProgressBar percent={100} color="#3bd4ca" />
 * ```
 */
export const ProgressBar = ({
  percent,
  height = 15,
  showInfo = false,
  color = colors.success[400],
  trackColor = colors.neutral[500],
  ...props
}: ProgressBarProps) => {
  // Clamp percent between 0 and 100
  const clampedPercent = Math.min(100, Math.max(0, percent));

  return (
    <Wrapper $height={height}>
      <AntProgress
        type="line"
        percent={clampedPercent}
        strokeColor={color}
        trailColor={trackColor}
        strokeWidth={height}
        showInfo={showInfo}
        {...props}
      />
    </Wrapper>
  );
};

export default ProgressBar;
